import { FC, PropsWithChildren, useCallback, useMemo, useRef } from 'react';

import { AdElementSVG, isSVGElement, isTextElement } from '@adflow/types';
import { indexObjArray, useEffectOnce, type ElementAnimationStyle } from '@adflow/utils';

import { ProgressContext, everyElementReady, noElementsToWaitFor } from '../../ProgressContext';
import { AdElementRenderProps } from '../../types';
import ImageElement from '../ImageElement';
import SVGElement from '../SVGElement';
import TeamKit from '../TeamKit';
import TextElement from '../TextElement';
import VectorElement from '../VectorElement';

export type AdArtworkProps = {
  adElements: Array<AdElementRenderProps>;
  isPreview?: boolean;
  svgIdPrefix?: string | null;
  animationOverrides?: ReadonlyMap<string, ElementAnimationStyle> | null;
  animationTargetIds?: ReadonlySet<string> | null;
};

/** Shared paint order for runtime, preview, and editor artwork. */
export function sortAdElementsForRender(
  adElements: readonly AdElementRenderProps[],
): AdElementRenderProps[] {
  return [...adElements].sort((a, b) => (a.position?.z ?? 0) - (b.position?.z ?? 0));
}

function ElementSelector({
  element,
  isPreview,
  svgIdPrefix,
  animationStyle,
  animationTargetId,
}: {
  element: AdElementRenderProps;
  isPreview?: boolean;
  svgIdPrefix?: string | null;
  animationStyle?: ElementAnimationStyle;
  animationTargetId?: string;
}) {
  if (element.type === 'SVG') {
    return (
      <SVGElement
        svgString={element.data.svg}
        animationStyle={animationStyle}
        animationTargetId={animationTargetId}
      />
    );
  }

  if (element.type === 'TEXT') {
    return (
      <TextElement
        id={element.id}
        elData={element.data}
        sources={element.sources}
        position={element.position}
        size={element.size}
        animationStyle={animationStyle}
        animationTargetId={animationTargetId}
      />
    );
  }

  if (element.type === 'TEAM_ASSET') {
    if (element.data.display?.type === 'kit') {
      return (
        <TeamKit
          position={element.position}
          size={element.size}
          kitProps={element.data.display.data}
          animationStyle={animationStyle}
          animationTargetId={animationTargetId}
        />
      );
    }

    if (element.data.display?.type === 'badge') {
      return (
        <ImageElement
          rotation={element.rotation}
          id={element.id}
          position={element.position}
          size={element.size}
          data={element.data.display.data}
          isPreview={isPreview}
          animationStyle={animationStyle}
          animationTargetId={animationTargetId}
        />
      );
    }
  }

  if (element.type === 'IMAGE' || element.type === 'CASINO_ASSET') {
    return (
      <ImageElement
        id={element.id}
        position={element.position}
        size={element.size}
        rotation={element.rotation}
        data={element.data}
        isPreview={isPreview}
        animationStyle={animationStyle}
        animationTargetId={animationTargetId}
      />
    );
  }

  if (element.type === 'VECTOR') {
    return (
      <VectorElement
        data={element.data}
        position={element.position}
        size={element.size}
        name={element.name}
        svgIdPrefix={svgIdPrefix}
        animationStyle={animationStyle}
        animationTargetId={animationTargetId}
      />
    );
  }

  return <></>;
}

/** Element tree shared by delivered ads and the unclipped editor stage. */
export const AdArtwork: FC<AdArtworkProps> = ({
  adElements,
  isPreview,
  svgIdPrefix,
  animationOverrides,
  animationTargetIds,
}) => {
  const sortedAdElements = useMemo(() => sortAdElementsForRender(adElements), [adElements]);

  const rootBackgroundVectors = useMemo(
    () =>
      sortedAdElements.filter(
        (el) => el.type === 'VECTOR' && (el.id.endsWith('-bg') || el.name.endsWith(' Background')),
      ),
    [sortedAdElements],
  );

  const foregroundAdElements = useMemo(() => {
    const rootBackgroundIds = new Set(rootBackgroundVectors.map((el) => el.id));
    return sortedAdElements.filter((el) => !rootBackgroundIds.has(el.id));
  }, [sortedAdElements, rootBackgroundVectors]);

  const elementsReadyState = useRef(
    indexObjArray(
      sortedAdElements.filter(isTextElement).map((el) => ({ id: el.id, ready: false })),
    ),
  );

  const updateElementReadyState = useCallback((id: string, ready: boolean) => {
    elementsReadyState.current[id] = {
      id: id,
      ready,
    };

    if (everyElementReady(elementsReadyState)) {
      window.adReady = true;
    }
  }, []);

  useEffectOnce(() => {
    if (noElementsToWaitFor(elementsReadyState)) {
      window.adReady = true;
    }
  });

  return (
    <ProgressContext.Provider value={{ elementsReadyState, updateElementReadyState }}>
      {rootBackgroundVectors.map((element) => (
        <ElementSelector
          key={element.id}
          element={element}
          isPreview={isPreview}
          svgIdPrefix={svgIdPrefix}
          animationStyle={animationOverrides?.get(element.id)}
          animationTargetId={animationTargetIds?.has(element.id) ? element.id : undefined}
        />
      ))}
      {foregroundAdElements?.map((element) => (
        <ElementSelector
          key={element.id}
          element={element}
          isPreview={isPreview}
          svgIdPrefix={svgIdPrefix}
          animationStyle={animationOverrides?.get(element.id)}
          animationTargetId={animationTargetIds?.has(element.id) ? element.id : undefined}
        />
      ))}
    </ProgressContext.Provider>
  );
};

AdArtwork.displayName = 'AdflowAdArtwork';

export function findRootSvgElement(
  adElements: readonly AdElementRenderProps[],
): AdElementSVG | undefined {
  return sortAdElementsForRender(adElements).find(isSVGElement);
}

type AdFrameProps = PropsWithChildren<{
  rootElement: AdElementSVG | undefined;
  clipToFrame: boolean;
}>;

export const AdFrame: FC<AdFrameProps> = ({ rootElement, clipToFrame, children }) => {
  if (!rootElement) {
    return null;
  }
  const width = rootElement.size.width;
  const height = rootElement.size.height;
  return (
    <div
      id="ad_root"
      data-clip-to-frame={clipToFrame ? undefined : 'false'}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        position: 'relative',
        overflow: clipToFrame ? 'hidden' : 'visible',
      }}
    >
      {children}
    </div>
  );
};

AdFrame.displayName = 'AdflowAdFrame';
